import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { creditsAPI } from '@/api/credits';

/**
 * Credit Context
 * Manages user credit status and limits
 */
const CreditContext = createContext(null);

/**
 * CreditProvider component
 * Wraps the application to provide credit state and functions
 */
export function CreditProvider({ children }) {
  const [creditStatus, setCreditStatus] = useState({
    monthly_credits_used: 0,
    monthly_credits_limit: 1500,
    remaining_monthly_credits: 1500,
    max_single_load: 300,
    credits_reset_month: new Date().getMonth() + 1
  });
  const [creditError, setCreditError] = useState(null);
  
  /**
   * Fetch current credit status from backend
   */
  const fetchCreditStatus = useCallback(async () => {
    try {
      const data = await creditsAPI.getCreditStatus();
      setCreditStatus(data);
      setCreditError(null);
    } catch (error) {
      console.error('Credit status fetch error:', error);
      setCreditError(error);
    }
  }, []);
  
  /**
   * Check if user has reached credit limit
   */
  const checkCreditLimit = useCallback(async (userId) => {
    try {
      const data = await creditsAPI.checkCreditLimit(userId);
      return data;
    } catch (error) {
      console.error('Credit limit check error:', error);
      throw error;
    }
  }, []);
  
  // Memoize context value
  const value = useMemo(() => ({
    creditStatus,
    creditError,
    fetchCreditStatus,
    checkCreditLimit
  }), [creditStatus, creditError, fetchCreditStatus, checkCreditLimit]);
  
  return (
    <CreditContext.Provider value={value}>
      {children}
    </CreditContext.Provider>
  );
}

/**
 * useCredit hook
 * Access credit context in components
 * @throws {Error} If used outside CreditProvider
 */
export const useCredit = () => {
  const context = useContext(CreditContext);
  if (!context) {
    throw new Error('useCredit must be used within CreditProvider');
  }
  return context;
};
